import type { CoachProfile } from '@efdigital/ll-addon-data';
import type { CoachProfileLanguageProficiency } from '@efdigital/ll-addon-data/types/coaching/fragments/coach-profile-language';

export const COACHING_PICKLIST_GROUPS = {
  specialties: 'coaching-specialisms',
  coachingTypes: 'coaching-types',
  industries: 'coaching-industries',
  countries: 'coaching-countries',
  levelsOfSeniority: 'coaching-seniority',
  language: 'language',
} as const;

const PROFICIENCY_LABELS: Record<CoachProfileLanguageProficiency, string> = {
  1: 'Basic',
  2: 'Conversational',
  3: 'Professional',
  4: 'Fluent',
  5: 'Native',
};

export function coachInitials(
  profile: Pick<CoachProfile, 'firstName' | 'lastName'>
): string {
  const letters = [profile.firstName, profile.lastName]
    .filter(Boolean)
    .map((name) => name[0])
    .join('')
    .toUpperCase();

  if (letters.length > 2) {
    return letters[0]! + letters[letters.length - 1]!;
  }
  if (letters.length === 0) {
    return '@';
  }
  return letters;
}

export function coachDisplayName(
  profile: Pick<CoachProfile, 'prefix' | 'firstName' | 'lastName' | 'suffix'>
): string {
  return [profile.prefix, profile.firstName, profile.lastName, profile.suffix]
    .filter(Boolean)
    .join(' ');
}

export function coachCurrentRole(
  profile: Pick<CoachProfile, 'workExperiences'>
): string | null {
  const experiences = profile.workExperiences;
  // A WarpDrive SchemaArray throws on out-of-bounds index access (a native
  // array would return undefined), so guard on length before indexing — a
  // coach with no work experience would otherwise crash the whole render.
  if (!experiences || experiences.length === 0) {
    return null;
  }
  const position = experiences[0]?.position?.trim();
  return position || null;
}

export function coachLocations(
  profile: Pick<CoachProfile, 'locations'>
): string | null {
  const locations = profile.locations?.filter(Boolean) ?? [];
  return locations.length > 0 ? locations.join(', ') : null;
}

export function languageProficiencyLabel(
  level: CoachProfileLanguageProficiency
): string {
  return PROFICIENCY_LABELS[level];
}

export function formatTimezoneDisplay(iana: string | null): {
  name: string;
  offset: string;
} | null {
  if (!iana) {
    return null;
  }

  try {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: iana,
      timeZoneName: 'shortOffset',
    });
    const parts = formatter.formatToParts(now);
    const offset =
      parts.find((part) => part.type === 'timeZoneName')?.value ?? '';

    return {
      name: iana.replace(/_/g, ' '),
      offset,
    };
  } catch {
    return {
      name: iana.replace(/_/g, ' '),
      offset: '',
    };
  }
}

export function formatCertificationDates(
  startDate: string,
  endDate: string
): string {
  const parts = [startDate, endDate].filter((value) => value.trim());
  return parts.join(' – ');
}

export function isLastItem(index: number, length: number): boolean {
  return index === length - 1;
}
